/**
 * providerLabel.ts — P3-D2 / Phase-3 visible provider trust
 *
 * One presenter for "which provider actually served this data", shared by every
 * surface that shows it. It exists because `D-P3AP-9` found the client making
 * affirmative Theta claims that are false when Polygon serves.
 *
 * ── Rules this module exists to enforce ───────────────────────────────────
 *  1. Render the SERVED provider, taken from governed response provenance.
 *     Nothing here derives provider identity from configuration, routing,
 *     plan level, request URL or error history — the server already knows,
 *     and the UI's only job is to say so.
 *  2. `[FALLBACK]` is driven by `fallbackActive` alone. P3-D2 banked TWO
 *     Polygon roles — authorized fallback AND complementary institutional
 *     context — so `provider === 'polygon'` does NOT mean a fallback
 *     happened. Marking every Polygon serve as fallback would be a new
 *     falsehood replacing the old one.
 *  3. Absence is neutral. When no provenance is available the answer is
 *     "unknown", never a default of Theta and never a default of Polygon.
 *     `D-P3P-8`'s absence-is-not-a-value discipline, applied to presentation.
 *  4. Nothing internal is surfaced: no failureClass, no reason code, no
 *     health flag. Visible trust needs the source, not the plumbing.
 */

/**
 * The governed provenance a served market-data response already carries.
 * A SIBLING of `SessionMeta`, never inside it — `SessionMeta` keeps its own
 * numeric contract (`D-P3T-4`).
 */
export interface ServedProvenance {
  provider:          string | null;
  requestedProvider: string | null;
  fallbackActive:    boolean;
}

/** Shown when no served provenance is available. Neutral by construction. */
export const UNKNOWN_PROVIDER_LABEL = 'Provider unknown';

/**
 * Display names for providers the product actually serves from. An
 * unrecognised identifier is returned as-is rather than being mapped onto a
 * known provider — a future provider must not be silently relabelled.
 */
const DISPLAY_NAMES: Readonly<Record<string, string>> = Object.freeze({
  thetadata:           'Theta Data',
  'thetadata-gateway': 'Theta Data',
  theta:               'Theta Data',
  polygon:             'Polygon',
});

export function providerDisplayName(provider: string | null | undefined): string {
  if (typeof provider !== 'string' || provider.length === 0) return UNKNOWN_PROVIDER_LABEL;
  return DISPLAY_NAMES[provider.toLowerCase()] ?? provider;
}

/**
 * The user-facing label for a served response.
 *
 *   Theta serving                     → "Theta Data"
 *   Polygon serving as fallback       → "Polygon [FALLBACK]"
 *   Polygon serving complementary     → "Polygon"
 *   nothing known yet                 → "Provider unknown"
 */
export function providerLabel(provenance: ServedProvenance | null | undefined): string {
  if (!provenance) return UNKNOWN_PROVIDER_LABEL;

  const name = providerDisplayName(provenance.provider);
  if (name === UNKNOWN_PROVIDER_LABEL) return UNKNOWN_PROVIDER_LABEL;

  // Strictly the governed flag. Never inferred from the provider name.
  return provenance.fallbackActive === true ? `${name} [FALLBACK]` : name;
}

/**
 * Narrow a raw served `meta` object to governed provenance, or `null` when the
 * response carried none. Returning `null` rather than a fabricated default is
 * what keeps "we do not know" distinguishable from "Theta served it".
 */
export function provenanceFrom(meta: unknown): ServedProvenance | null {
  if (!meta || typeof meta !== 'object') return null;
  const m = meta as Record<string, unknown>;
  if (typeof m.provider !== 'string' || m.provider.length === 0) return null;
  return {
    provider:          m.provider,
    requestedProvider: typeof m.requestedProvider === 'string' ? m.requestedProvider : null,
    fallbackActive:    m.fallbackActive === true,
  };
}
